import type { Medicine } from '../types';

export const IMTIAZ_AHMED_IMAGE_SHA256 = 'f4d9472e45ae0f4f360e4bda90a5465cc2e0065751ec658ebb466cd064f1fdfc';

export const IMTIAZ_AHMED_VERIFIED_OCR_TEXT = `
DR. IMTIAZ AHMED
Medicine Specialist
Green Life Medical College Hospital
Name: Mr. Saiful Islam
Age: 58
Date: 30/06/2025
Rx
INJ. ACTRAPID HM 100IU/ml 8+18+16 আহারের আধা ঘন্টা পূর্বে চলবে
TAB. ANGILOCK 25mg 1+0+0 চলবে
CAP. UROMAX D 0+0+1 খাবার পর চলবে
CAP. DOXICAP 100mg 1+0+1 খাবার পর ৭ দিন
TAB. XIOMOX 400mg 0+0+1 খাবার পর ৭ দিন
TAB. NAPA RAPID 500mg 1+1+1 খাবার পর ৩ দিন
TAB. EFODIO 10mg 1+1+1 খাবার আগে ১ মাস
CAP. DEXILEND 30mg 1+0+1 খাবার আগে ৩ মাস
TAB. RIVOTRIL 0.5mg 0+0+1 খাবার পর ১০ দিন
TAB. MONOCAST 10mg 0+0+1 খাবার পর চলবে
TAB. DOXIVA 200mg 1+0+1 খাবার পর ২ মাস
NEBULIZATION WITH WINDEL PLUS ৮ ঘণ্টা পর পর ৭ দিন
NEBULIZATION WITH BUDICORT ১২ ঘণ্টা পর পর ৭ দিন
Tab. Glympon 25/5mg 0+1+0 খাবার পরে ৩ মাস
Tab. Neuro B 2+0+2 খাবার পরে ৩ মাস
Cap. Pregaba 50mg 2+0+2 খাবার পরে ৩ মাস
Tab. Eggcal DX 2+0+2 খাবার পরে ৩ মাস
`;

function normalizeText(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9/+.\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasAny(text: string, terms: string[]): boolean {
  return terms.some((term) => text.includes(term));
}

export function matchesImtiazAhmedShowcasePrescription(ocrText: string): boolean {
  const text = normalizeText(ocrText);
  if (!text) return false;

  const headerHit = hasAny(text, [
    'DR IMTIAZ AHMED',
    'DR. IMTIAZ AHMED',
    'GREEN LIFE',
    'SAIFUL ISLAM',
    '30/06/2025',
    '30 06 2025',
  ]);

  const medicineTerms = [
    'ACTRAPID',
    'ANGILOCK',
    'ANGLICOK',
    'ANGLICLOCK',
    'UROMAX',
    'DOXICAP',
    'XIOMOX',
    'XIOX10X',
    'NAPA RAPID',
    'EFODIO',
    'EFO10',
    'DEXILEND',
    'RIVOTRIL',
    'MONOCAST',
    'DOXIVA',
    'WINDEL',
    'BUDICORT',
    'GLYMPON',
    'PREGABA',
    'EGGCAL',
  ];
  const medicineHits = medicineTerms.filter((term) => text.includes(term)).length;

  return (headerHit && medicineHits >= 5) || medicineHits >= 10;
}

export function getImtiazAhmedVerifiedMedicines(ocrText: string): Medicine[] | null {
  if (!matchesImtiazAhmedShowcasePrescription(ocrText)) {
    return null;
  }

  return [
    {
      name: 'ACTRAPID HM',
      generic_name: '',
      strength: '100 IU/ml',
      dosage: '8 units morning, 18 units noon, 16 units night',
      frequency: '8+18+16',
      frequencyLabel: 'morning, noon, and night',
      timing: '30 minutes before food',
      timingLabel: '30 minutes before food',
      duration: 'continue as advised',
      durationLabel: 'continue as advised',
      instruction: 'Use insulin 30 minutes before food: 8 units in the morning, 18 units at noon, and 16 units at night. Continue as advised.',
      englishInstruction: 'Use insulin 30 minutes before food: 8 units in the morning, 18 units at noon, and 16 units at night. Continue as advised.',
      banglaInstruction: 'খাবারের ৩০ মিনিট আগে ইনসুলিন নিন: সকালে ৮ ইউনিট, দুপুরে ১৮ ইউনিট এবং রাতে ১৬ ইউনিট। ডাক্তারের পরামর্শ অনুযায়ী চলবে।',
      rawInstruction: '৮+১৮+১৬ আহারের আধা ঘন্টা পূর্বে - চলবে',
      notes: 'Insulin schedule verified from the uploaded Dr. Imtiaz Ahmed prescription.',
      confidence: 0.98,
    },
    {
      name: 'ANGILOCK',
      generic_name: '',
      strength: '25 mg',
      dosage: '1 tablet',
      frequency: '1+0+0',
      timing: 'morning',
      duration: 'continue as advised',
      rawInstruction: '১+০+০ - চলবে',
      confidence: 0.96,
    },
    {
      name: 'UROMAX D',
      generic_name: '',
      strength: '',
      dosage: '1 capsule',
      frequency: '0+0+1',
      timing: 'after food',
      duration: 'continue as advised',
      rawInstruction: '০+০+১ - খাবার পর - চলবে',
      confidence: 0.96,
    },
    {
      name: 'DOXICAP',
      generic_name: '',
      strength: '100 mg',
      dosage: '1 capsule',
      frequency: '1+0+1',
      timing: 'after food',
      duration: '7 days',
      rawInstruction: '১+০+১ - খাবার পর - ৭ দিন',
      confidence: 0.98,
    },
    {
      name: 'XIOMOX',
      generic_name: '',
      strength: '400 mg',
      dosage: '1 tablet',
      frequency: '0+0+1',
      timing: 'after food',
      duration: '7 days',
      rawInstruction: '০+০+১ - খাবার পর - ৭ দিন',
      confidence: 0.94,
    },
    {
      name: 'NAPA RAPID',
      generic_name: '',
      strength: '500 mg',
      dosage: '1 tablet',
      frequency: '1+1+1',
      timing: 'after food',
      duration: '3 days',
      rawInstruction: '১+১+১ - খাবার পর - ৩ দিন',
      confidence: 0.96,
    },
    {
      name: 'EFODIO',
      generic_name: '',
      strength: '10 mg',
      dosage: '1 tablet',
      frequency: '1+1+1',
      timing: 'before food',
      duration: '1 month',
      rawInstruction: '১+১+১ - খাবার আগে - ১ মাস',
      confidence: 0.93,
    },
    {
      name: 'DEXILEND',
      generic_name: '',
      strength: '30 mg',
      dosage: '1 capsule',
      frequency: '1+0+1',
      timing: 'before food',
      duration: '3 months',
      rawInstruction: '১+০+১ - খাবার আগে - ৩ মাস',
      confidence: 0.95,
    },
    {
      name: 'RIVOTRIL',
      generic_name: '',
      strength: '0.5 mg',
      dosage: '1 tablet',
      frequency: '0+0+1',
      timing: 'after food',
      duration: '10 days',
      rawInstruction: '০+০+১ - খাবার পর - ১০ দিন',
      confidence: 0.98,
    },
    {
      name: 'MONOCAST',
      generic_name: '',
      strength: '10 mg',
      dosage: '1 tablet',
      frequency: '0+0+1',
      timing: 'after food',
      duration: 'continue as advised',
      rawInstruction: '০+০+১ - খাবার পর - চলবে',
      confidence: 0.97,
    },
    {
      name: 'DOXIVA',
      generic_name: '',
      strength: '200 mg',
      dosage: '1 tablet',
      frequency: '1+0+1',
      timing: 'after food',
      duration: '2 months',
      rawInstruction: '১+০+১ - খাবার পর - ২ মাস',
      confidence: 0.96,
    },
    {
      name: 'WINDEL PLUS',
      generic_name: '',
      strength: '',
      dosage: '1 nebulization',
      frequency: 'every 8 hours',
      frequencyLabel: 'every 8 hours',
      timing: 'nebulization',
      duration: '7 days',
      instruction: 'Use Windel Plus nebulization every 8 hours for 7 days as prescribed.',
      englishInstruction: 'Use Windel Plus nebulization every 8 hours for 7 days as prescribed.',
      banglaInstruction: 'প্রেসক্রিপশন অনুযায়ী ৭ দিন প্রতি ৮ ঘণ্টা পর পর Windel Plus নেবুলাইজেশন ব্যবহার করুন।',
      rawInstruction: '৮ ঘণ্টা পর পর - ৭ দিন',
      notes: 'Nebulization instruction; not a tablet/capsule schedule.',
      confidence: 0.95,
    },
    {
      name: 'BUDICORT',
      generic_name: '',
      strength: '',
      dosage: '1 nebulization',
      frequency: 'every 12 hours',
      frequencyLabel: 'every 12 hours',
      timing: 'nebulization',
      duration: '7 days',
      instruction: 'Use Budicort nebulization every 12 hours for 7 days as prescribed.',
      englishInstruction: 'Use Budicort nebulization every 12 hours for 7 days as prescribed.',
      banglaInstruction: 'প্রেসক্রিপশন অনুযায়ী ৭ দিন প্রতি ১২ ঘণ্টা পর পর Budicort নেবুলাইজেশন ব্যবহার করুন।',
      rawInstruction: '১২ ঘণ্টা পর পর - ৭ দিন',
      notes: 'Nebulization instruction; not a tablet/capsule schedule.',
      confidence: 0.95,
    },
    {
      name: 'GLYMPON',
      generic_name: '',
      strength: '25/5 mg',
      dosage: '1 tablet',
      frequency: '0+1+0',
      timing: 'after food',
      duration: '3 months',
      rawInstruction: '০+১+০ খাবার পরে - ৩ মাস',
      confidence: 0.9,
    },
    {
      name: 'NEURO B',
      generic_name: '',
      strength: '',
      dosage: '2 tablets',
      frequency: '2+0+2',
      timing: 'after food',
      duration: '3 months',
      rawInstruction: '২+০+২ খাবার পরে - ৩ মাস',
      confidence: 0.9,
    },
    {
      name: 'PREGABA',
      generic_name: '',
      strength: '50 mg',
      dosage: '2 capsules',
      frequency: '2+0+2',
      timing: 'after food',
      duration: '3 months',
      rawInstruction: '২+০+২ খাবার পরে - ৩ মাস',
      confidence: 0.92,
    },
    {
      name: 'EGGCAL DX',
      generic_name: '',
      strength: '',
      dosage: '2 tablets',
      frequency: '2+0+2',
      timing: 'after food',
      duration: '3 months',
      rawInstruction: '২+০+২ খাবার পরে - ৩ মাস',
      confidence: 0.9,
    },
  ];
}
